import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Package, Plus, Pencil, Trash2, AlertTriangle, Search, BookOpen } from "lucide-react";

const EMPTY = {
  denumire: "",
  categorie: "substanță",
  servicii: [],
  aviz_lot: "",
  aviz_autorizatie: "",
  substanta_activa: "",
  consum_concentrata: "",
  consum_total: "",
  timp_actiune: "",
  cantitate: "",
  unitate: "buc",
  prag_alarma: "",
  observatii: "",
};

const ALL_SERVICES = ["DEZINSECȚIE", "DERATIZARE", "DEZINFECȚIE"];

const svcBadge = (svc) => {
  const map = {
    "DEZINSECȚIE": "bg-blue-100 text-blue-800 border-blue-200",
    "DERATIZARE": "bg-amber-100 text-amber-800 border-amber-200",
    "DEZINFECȚIE": "bg-emerald-100 text-emerald-800 border-emerald-200",
  };
  return map[svc] || "bg-slate-100 text-slate-700 border-slate-200";
};

export default function Stocks() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // Admin sees consumption breakdown; non-admin sees plain list
      const path = isAdmin ? "/stocks/consumption" : "/stocks";
      const { data } = await api.get(path);
      setRows(data);
    } catch (e) {
      console.error("Failed to load stocks:", e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) => {
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return (
      (r.denumire || "").toLowerCase().includes(t) ||
      (r.categorie || "").toLowerCase().includes(t) ||
      (r.aviz_lot || "").toLowerCase().includes(t)
    );
  });

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      denumire: r.denumire || "",
      categorie: r.categorie || "substanță",
      servicii: r.servicii || [],
      aviz_lot: r.aviz_lot || "",
      aviz_autorizatie: r.aviz_autorizatie || "",
      substanta_activa: r.substanta_activa || "",
      consum_concentrata: r.consum_concentrata || "",
      consum_total: r.consum_total || "",
      timp_actiune: r.timp_actiune || "",
      cantitate: r.cantitate != null ? String(r.cantitate) : "",
      unitate: r.unitate || "buc",
      prag_alarma: r.prag_alarma != null ? String(r.prag_alarma) : "",
      observatii: r.observatii || "",
    });
    setOpen(true);
  };

  const toggleSvc = (svc) =>
    setForm((f) => ({
      ...f,
      servicii: f.servicii.includes(svc) ? f.servicii.filter((s) => s !== svc) : [...f.servicii, svc],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.denumire.trim()) {
      toast.error("Denumirea este obligatorie");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        cantitate: parseFloat(form.cantitate || 0),
        prag_alarma: parseFloat(form.prag_alarma || 0),
      };
      if (editing) {
        await api.patch(`/stocks/${editing.id}`, payload);
        toast.success("Stoc actualizat");
      } else {
        await api.post("/stocks", payload);
        toast.success("Stoc adăugat");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare salvare");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Ștergi „${r.denumire}"?`)) return;
    try {
      await api.delete(`/stocks/${r.id}`);
      toast.success("Șters");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
    }
  };

  return (
    <div className="space-y-6" data-testid="stocks-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="label-eyebrow">Inventar</p>
          <h1 className="font-display font-black text-4xl tracking-tight mt-2">Stocuri</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Substanțe, echipamente și consumabile. {isAdmin ? "Editabil doar de admin." : "Doar vizualizare — administrarea e făcută de admin."}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={openNew} className="btn-gold hover:btn-gold" data-testid="add-stock-btn">
            <Plus className="w-4 h-4 mr-2" /> Adaugă stoc
          </Button>
        )}
      </header>

      <div className="relative max-w-lg">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Caută după denumire, categorie sau lot..."
          className="pl-9 h-11 bg-white"
          data-testid="stocks-search"
        />
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Se încarcă...</p>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-border rounded-md p-12 text-center">
          <Package className="w-10 h-10 mx-auto text-muted-foreground" />
          <p className="mt-3 font-medium">Niciun stoc înregistrat</p>
          {isAdmin && (
            <Button onClick={openNew} className="btn-gold hover:btn-gold mt-4">
              <Plus className="w-4 h-4 mr-2" /> Adaugă primul stoc
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white border border-border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-border">
              <tr className="text-left">
                <th className="px-4 py-3 label-eyebrow">Denumire</th>
                <th className="px-4 py-3 label-eyebrow">Categorie</th>
                <th className="px-4 py-3 label-eyebrow">Aviz Lot</th>
                <th className="px-4 py-3 label-eyebrow">Cantitate</th>
                <th className="px-4 py-3 label-eyebrow">Prag alarmă</th>
                {isAdmin && <th className="px-4 py-3 label-eyebrow">Consum / agent</th>}
                {isAdmin && <th className="px-4 py-3 label-eyebrow text-right">Acțiuni</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((r) => {
                const low = r.prag_alarma > 0 && r.cantitate <= r.prag_alarma;
                return (
                  <tr key={r.id} data-testid={`stock-row-${r.id}`} className={low ? "bg-red-50/40" : ""}>
                    <td className="px-4 py-3 font-medium">
                      <div className="flex items-center gap-2">
                        {low && <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />}
                        {r.denumire}
                      </div>
                      {r.observatii && <p className="text-xs text-muted-foreground mt-0.5">{r.observatii}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-1 bg-slate-100 rounded font-medium">{r.categorie || "—"}</span>
                      {r.servicii && r.servicii.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {r.servicii.map((sv) => (
                            <span key={sv} className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold ${svcBadge(sv)}`}>{sv}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{r.aviz_lot || "—"}</td>
                    <td className={`px-4 py-3 font-mono ${low ? "text-red-700 font-bold" : ""}`}>
                      {r.cantitate} {r.unitate}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                      {r.prag_alarma > 0 ? `${r.prag_alarma} ${r.unitate}` : "—"}
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3">
                        {(!r.consum_agenti || r.consum_agenti.length === 0) ? (
                          <span className="text-xs text-muted-foreground">—</span>
                        ) : (
                          <div className="space-y-0.5">
                            <p className="text-xs font-bold text-gold-deep">
                              Total: {r.total_consumat} {r.unitate}
                            </p>
                            {r.consum_agenti.map((a) => (
                              <p key={a.agent} className="text-[11px] text-muted-foreground">
                                <span className="font-medium text-foreground">{a.agent}</span>: {a.cantitate} {r.unitate}
                              </p>
                            ))}
                          </div>
                        )}
                      </td>
                    )}
                    {isAdmin && (
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <Link to={`/stocuri/${r.id}/fisa`}>
                            <Button size="sm" variant="outline" data-testid={`fisa-stock-${r.id}`} title="Fișă de magazie">
                              <BookOpen className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`edit-stock-${r.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => remove(r)}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            data-testid={`delete-stock-${r.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="w-[95vw] max-w-lg max-h-[85dvh] xl:max-h-[92dvh] overflow-y-auto"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing ? "Modifică stoc" : "Adaugă stoc"}
            </DialogTitle>
            <DialogDescription>
              Doar administratorul poate crea / modifica / șterge stocuri.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="label-eyebrow">Denumire *</Label>
              <Input
                value={form.denumire}
                onChange={(e) => setForm({ ...form, denumire: e.target.value })}
                className="mt-2 h-11"
                placeholder="ex: K-OTHRINE Partix 25 SC"
                required
                data-testid="stock-denumire-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-eyebrow">Categorie</Label>
                <select
                  value={form.categorie}
                  onChange={(e) => setForm({ ...form, categorie: e.target.value })}
                  className="mt-2 h-11 w-full border border-input rounded-md bg-white px-3 text-sm"
                  data-testid="stock-categorie-input"
                >
                  <option value="substanță">Substanță (biocid)</option>
                  <option value="echipament">Echipament</option>
                  <option value="consumabil">Consumabil</option>
                  <option value="EPP">EPP (Protecție)</option>
                  <option value="altele">Altele</option>
                </select>
              </div>
              <div>
                <Label className="label-eyebrow">Aviz Lot (număr lot fabricație)</Label>
                <Input
                  value={form.aviz_lot}
                  onChange={(e) => setForm({ ...form, aviz_lot: e.target.value })}
                  className="mt-2 h-11"
                  placeholder="ex: LOT 1234/2026"
                  data-testid="stock-aviz-lot"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="label-eyebrow">Cantitate</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.cantitate}
                  onChange={(e) => setForm({ ...form, cantitate: e.target.value })}
                  className="mt-2 h-11"
                  placeholder="0"
                />
              </div>
              <div>
                <Label className="label-eyebrow">Unitate</Label>
                <select
                  value={form.unitate}
                  onChange={(e) => setForm({ ...form, unitate: e.target.value })}
                  className="mt-2 h-11 w-full border border-input rounded-md bg-white px-3 text-sm"
                >
                  <option value="buc">buc</option>
                  <option value="l">litri</option>
                  <option value="ml">ml</option>
                  <option value="kg">kg</option>
                  <option value="g">g</option>
                  <option value="pachete">pachete</option>
                </select>
              </div>
              <div>
                <Label className="label-eyebrow">Prag alarmă</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.prag_alarma}
                  onChange={(e) => setForm({ ...form, prag_alarma: e.target.value })}
                  className="mt-2 h-11"
                  placeholder="0"
                />
              </div>
            </div>
            <div>
              <Label className="label-eyebrow">Substanța activă / compoziție</Label>
              <Input
                value={form.substanta_activa}
                onChange={(e) => setForm({ ...form, substanta_activa: e.target.value })}
                className="mt-2 h-11"
                placeholder="ex: Deltametrin 25 g/L (25 SC)"
                data-testid="stock-substanta-activa"
              />
            </div>
            <div>
              <Label className="label-eyebrow">Aviz Autorizație</Label>
              <Input
                value={form.aviz_autorizatie}
                onChange={(e) => setForm({ ...form, aviz_autorizatie: e.target.value })}
                className="mt-2 h-11"
                placeholder="ex: 3421BIO/01/12.24"
                data-testid="stock-aviz-autorizatie"
              />
            </div>
            <div>
              <Label className="label-eyebrow">Folosită pentru serviciile</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {ALL_SERVICES.map((svc) => {
                  const active = form.servicii.includes(svc);
                  return (
                    <button
                      key={svc}
                      type="button"
                      onClick={() => toggleSvc(svc)}
                      data-testid={`stock-svc-${svc}`}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-md border transition-colors ${active ? svcBadge(svc) + " ring-2 ring-offset-1 ring-current" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                    >
                      {svc}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                Alege una sau mai multe. Determină ce apare la selectorul de substanțe din PV.
              </p>
            </div>
            <div>
              <Label className="label-eyebrow">Observații</Label>
              <Textarea
                value={form.observatii}
                onChange={(e) => setForm({ ...form, observatii: e.target.value })}
                className="mt-2"
                rows={3}
                placeholder="Producător, data expirării, note interne..."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Anulează</Button>
              <Button type="submit" disabled={saving} className="btn-gold hover:btn-gold" data-testid="save-stock-btn">
                {saving ? "Se salvează..." : "Salvează"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
